import { useQuery } from '@tanstack/react-query';

import type { GetMarkBackfillAccessoriesRequestedResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get backfill accessories requested status from a purchased vehicle
 */
export const getMarkBackfillAccessoriesRequested = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetMarkBackfillAccessoriesRequestedResponse> => {
	const response = await apiClient.get<GetMarkBackfillAccessoriesRequestedResponse>(
		ordersEndpoints.markBackfillAccessoriesRequested(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting backfill accessories requested status from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetMarkBackfillAccessoriesRequested = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.markBackfillAccessoriesRequested.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error(
					'Unable to get backfill accessories requested status without an order ID.',
				);
			}
			return getMarkBackfillAccessoriesRequested(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
